import useConditionalEffect from 'hooks/useConditionalEffect'
import { Feed } from 'interfaces'
import { ReactNode, useCallback, useRef } from 'react'
import { connect } from 'react-redux'
import { Observable, animationFrameScheduler, from, mergeMap, observeOn, of, throwError } from 'rxjs'
import { getOriginalFeed } from 'services/content/feeds/actions'
import { StoreState, StoreThunkDispatch } from 'store/state'

export interface FetchFeedProps {
  id: string
  children?: ReactNode
  onFetched(feed: Feed): void
  onFailed?(err: any): void
}

interface ConnectedFetchFeedProps extends FetchFeedProps {
  getOriginalFeed(): Observable<Feed>
}

const FetchFeed = ({
  id,
  children,
  onFetched,
  onFailed,
  getOriginalFeed: getOriginalFeedAction
}: ConnectedFetchFeedProps) => {
  const subscriptionRef = useRef<any>(null)

  const handleFailed = useCallback((err: any) => {
    console.log(err)
    if (onFailed) {
      onFailed(err)
    }
  }, [onFailed])

  const fetch = useCallback(() => {
    return getOriginalFeedAction()
      .pipe(
        mergeMap((feed: Feed) => {
          if (feed.type === 'unsupported') {
            return throwError(() => new Error('Feed type is not supported'))
          }

          return of(feed)
        }),
        observeOn(animationFrameScheduler)
      )
      .subscribe({ next: onFetched, error: handleFailed })
  }, [getOriginalFeedAction, onFetched, handleFailed])

  useConditionalEffect(([_fetch]) => {
    subscriptionRef.current = _fetch()

    return () => {
      subscriptionRef.current?.unsubscribe()
    }
  }, [id], [fetch])

  return children || null
}

function mapStateToProps(_state: StoreState, ownProps: FetchFeedProps) {
  return ownProps
}

function mapDispatchToProps(dispatch: StoreThunkDispatch, ownProps: FetchFeedProps) {
  const { id } = ownProps

  return {
    getOriginalFeed: () => from(dispatch(getOriginalFeed(id)).unwrap())
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(FetchFeed)
